// Hideo - script editor, Fountain mode: the screenplay font, the .fountain download and the PDF (the browser's print of
// the formatted pages, in a hidden frame, so the app itself is not printed). The page layout is the .fn-* CSS of
// legacy.css, which the frame takes from the app's own stylesheets; the paper is US Letter or A4, with no margins of
// the printer: the margins are the page's own (1.5 in left, 1 in at the top and on the right).
import { parseFountain } from './fountain'
import { screenplayHtml } from './fountainHtml'
import { downloadText } from '../desktop'

/** Courier Prime (the screenplay font, from Google Fonts like the editor's fonts), loaded the first time it is needed */
const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Courier+Prime:ital,wght@0,400;0,700;1,400;1,700&display=swap'
let fontLoaded = false
export function loadScreenplayFont(): void {
  if (fontLoaded) return
  fontLoaded = true
  const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = FONT_HREF; document.head.appendChild(l)
}

/** File name of an export: the title without characters that file systems refuse */
export const fileName = (title: string, ext: string): string => (title.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim() || 'script') + ext

/** Downloads the Fountain text as "<title>.fountain"; a script without a title page gets "Title: <title>" on top */
export function downloadFountain(src: string, title: string): void {
  const head = !parseFountain(src).title.length && title.trim() ? 'Title: ' + title.trim() + '\n\n' : ''
  downloadText(fileName(title, '.fountain'), head + src, 'text/plain')
}

export type Paper = 'letter' | 'a4'

/** Prints the screenplay (the browser's dialog, where "Save as PDF" makes the PDF): title page first, page numbers top
 *  right. The frame goes away after printing */
export function printScreenplay(src: string, title: string, paper: Paper): void {
  loadScreenplayFont()
  const frame = document.createElement('iframe')
  frame.setAttribute('aria-hidden', 'true')
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden'
  document.body.appendChild(frame)
  const doc = frame.contentDocument!, win = frame.contentWindow!
  // The app's stylesheets (the .fn-* rules and the font) and the print rules
  const styles = [...document.querySelectorAll('link[rel="stylesheet"], style')].map(n => n.outerHTML).join('')
  const size = paper === 'a4' ? 'A4' : 'letter'
  doc.open()
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><title></title>${styles}<style>@page{size:${size};margin:0}` +
    'html,body{margin:0;padding:0;background:#fff!important}.fn-paper{font-size:12pt}' +
    '.fn-page{margin:0;box-shadow:none;break-after:page;min-height:calc(var(--fn-h) - 2px)}.fn-page:last-child{break-after:auto}</style></head>' +
    `<body><div class="fn-paper${paper === 'a4' ? ' fn-a4' : ''}"></div></body></html>`)
  doc.close()
  doc.title = title.trim() || 'script'
  ;(doc.querySelector('.fn-paper') as HTMLElement).innerHTML = screenplayHtml(parseFountain(src), { screen: false, fallbackTitle: title.trim() })
  const done = () => setTimeout(() => frame.remove(), 500)
  win.addEventListener('afterprint', done)
  const go = () => { win.focus(); win.print(); setTimeout(done, 60000) }
  // Printed once the stylesheets and the font are there (at most 3 s)
  Promise.race([Promise.all([...doc.querySelectorAll('link')].map(l => new Promise(r => { l.addEventListener('load', r); l.addEventListener('error', r) }))).then(() => doc.fonts.ready), new Promise(r => setTimeout(r, 3000))]).then(go)
}
